import BaseListStore from 'stores/BaseListStore';
import systemNotificationStore, { ISystemNotification } from 'stores/systemNotification/SystemNotificationStore';
import { SortDirection } from 'constants/SortDirection';
import { getConditions, getDefaultFilter, getListConditions } from 'stores/_services/filterService';
import { IDataFetchOptions } from '../../types';
import { buildWhereFilter, get, getCount, getPage, post } from 'stores/_services/dataService';
import { runInAction, observable, makeObservable } from 'mobx';

const SEARCH_FIELDS = ['message'];

export default class SystemNotificationListStore extends BaseListStore<systemNotificationStore> {
    activeSystemNotificationCount = 0;
    menuIds: string[] = [];
    menuData = new Map<string, systemNotificationStore>();
    menusystemNotifications = <any>[];

    constructor() {
        super({
            itemStore: systemNotificationStore,
            sortBy: 'createdOn',
            sortDirection: SortDirection.DESC,
            defaultFilter: getDefaultFilter({ filters: ['archived'] })
        });

        makeObservable(this, {
            activeSystemNotificationCount: observable,
            menuIds: observable,
            menuData: observable,
            menusystemNotifications: observable
        });
    }

    async getsystemNotifications(args: IDataFetchOptions = {}, pageNumber = 1, pageSize = 25): Promise<void> {
        const shouldReset = this._updateListState(args);
        const conditions = getConditions(this.filter);

        const options = {
            filter: {
                limit: pageSize,
                skip: (pageNumber - 1) * pageSize,
                searchFields: SEARCH_FIELDS,
                where: conditions,
                order: this.sort
            }
        };

        const { data } = await getPage('systemNotifications', options);
        const totalCount = await getCount('systemNotifications', { where: conditions, searchFields: SEARCH_FIELDS });

        if (shouldReset) {
            this.clearIds();
        }

        this._updateData(data);
        this._updateIds(data, 0);
        //every step that updates observables in an asynchronous process should be marked as action(one way to use is runInAction), Only synchronous changes to observable data works in actions
        runInAction(() => {
            this.count = data.length;
            this.totalCount = totalCount;
            this.page = pageNumber;
            this.pageSize = pageSize;
        });
    }

    async getsystemNotification(systemNotificationId: string): Promise<void> {
        const options = {
            filter: {
                where: { id: systemNotificationId }
            }
        };

        const { data } = await get('systemNotifications', options);

        this._updateData(data);
    }

    async getActivesystemNotificationCount(): Promise<void> {
        const count = await getCount('systemNotifications', { where: { archived: false } });
        //every step that updates observables in an asynchronous process should be marked as action(one way to use is runInAction), Only synchronous changes to observable data works in actions
        runInAction(() => {
            this.activeSystemNotificationCount = count;
        });
    }

    loadMenuNotifications() {
        const data = this.menuIds.map((id) => this.menuData.get(id));

        runInAction(() => {
            this.menusystemNotifications = data;
        });
    }

    async getMenusystemNotifications(): Promise<void> {
        const options = {
            filter: {
                where: { archived: false },
                limit: 20,
                order: 'createdOn DESC'
            }
        };

        const { data } = await get('systemNotifications', options);

        this._updateMenuData(data);
        this._updateMenuIds(data);
        this.loadMenuNotifications();
    }

    private _updateMenuData(data: ISystemNotification[]) {
        this.menuData = new Map<string, systemNotificationStore>();

        data.forEach((t: ISystemNotification) => {
            if (t) {
                const previousItem = this.menuData.get(t.id);
                // @ts-ignore ItemStore is guaranteed to be a class
                runInAction(() => {
                    this.menuData.set(t.id, new this.ItemStore(t));
                });
            }
        });
    }

    _updateMenuIds(data: ISystemNotification[] = []): void {
        this.menuIds = [];

        for (let i = 0; i < data.length; i++) {
            if (data[i]) {
                this.menuIds.push(data[i].id);
            }
        }
    }

    async archivesystemNotification(id: string, fetchList = false, pageNumber = 1, pageSize = 25): Promise<void> {
        const options = {
            queryParams: { where: { id } }
        };

        await post('systemNotifications/archive', null, options);

        this.getActivesystemNotificationCount();
        fetchList ? this.getsystemNotifications({ reset: true }, pageNumber, pageSize) : this.getsystemNotification(id);

        await this.getMenusystemNotifications();
    }

    async archiveAll(pageSize = 25): Promise<void> {
        const conditions = getListConditions(this.selectedIdList, this.filter);

        const where = buildWhereFilter({
            where: conditions,
            searchFields: SEARCH_FIELDS
        });

        await post('systemNotifications/archive', null, { queryParams: { where } });

        this.deselectAll();
        await this.getActivesystemNotificationCount();
        await this.getsystemNotifications({ reset: true }, 1, pageSize);
        await this.getMenusystemNotifications();
    }

    async unarchivesystemNotification(id: string, fetchList = false, pageNumber = 1, pageSize = 25): Promise<void> {
        const options = {
            queryParams: { where: { id } }
        };

        await post('systemNotifications/unarchive', null, options);

        await this.getActivesystemNotificationCount();
        fetchList ? await this.getsystemNotifications({ reset: true }, pageNumber, pageSize) : await this.getsystemNotification(id);
        await this.getMenusystemNotifications();
    }

    async unarchiveAll(pageSize = 25): Promise<void> {
        const conditions = getListConditions(this.selectedIdList, this.filter);

        const where = buildWhereFilter({
            where: conditions,
            searchFields: SEARCH_FIELDS
        });

        await post('systemNotifications/unarchive', null, { queryParams: { where } });

        this.deselectAll();
        await this.getActivesystemNotificationCount();
        await this.getsystemNotifications({ reset: true }, 1, pageSize);
        await this.getMenusystemNotifications();
    }
}
